import React, { useContext, useEffect, useState } from 'react';
import styled from 'styled-components';
import Image from 'next/image';
import { resolvePath } from '@/utils/resolvePath';
import { replaceFieldsWithValues } from '../replaceFieldsWithValues';
import { trackEventAPI } from '@/analytics/middleware';
import { TAP_BTN_FAQ } from '@/analytics/events';
import { DeviceContext } from '@/pages/_app';

const Faq = ({
    faqs,
    location,
    brand,
    nearYou,
    city,
    garage,
}: {
    faqs: any;
    location?: any;
    brand?: any;
    nearYou?: any;
    city?: any;
    garage?: any;
}) => {
    const { heading, Faqs = [] } = faqs;
    const [activeIndex, setActiveIndex] = useState(
        new Array(Faqs?.length).fill(true)
    );
    const isRobot = useContext(DeviceContext).isRobot;

    const structfaq = Faqs?.map((faqItem: any) => ({
        '@type': 'Question',
        name: faqItem.text,
        acceptedAnswer: {
            '@type': 'Answer',
            text: faqItem.subText,
        },
    }));

    const faqPageSchema = {
        '@context': 'https://schema.org/',
        '@type': 'FAQPage',
        mainEntity: structfaq,
    };

    useEffect(() => {
        if (!isRobot) {
            setActiveIndex(new Array(Faqs?.length).fill(false));
        }
    }, []);

    const toggleFaq = async (index: number) => {
        await trackEventAPI({
            event_name: TAP_BTN_FAQ,
            customAttributes: {
                faq_question: Faqs[index].text,
            },
            isFakeDoor: true,
        });
        setActiveIndex((prevState) => {
            const newState = [...prevState];

            newState[index] = !newState[index];
            if (newState[index]) {
                newState.forEach((_, i) => {
                    if (i !== index) newState[i] = false;
                });
            }

            return newState;
        });
    };

    return (
        <FaqWrapper>
            {faqPageSchema?.mainEntity?.length > 0 && (
                <script
                    type="application/ld+json"
                    dangerouslySetInnerHTML={{
                        __html: JSON.stringify(faqPageSchema),
                    }}
                />
            )}
            <FaqHeading>
                {replaceFieldsWithValues(heading, {
                    location,
                    brand,
                    nearYou,
                    city,
                    garage,
                })}
            </FaqHeading>
            <FaqQAWrapper>
                {Faqs.map((collapsable: any, index: number) => {
                    return (
                        <FaqQAContainer key={index}>
                            <CollapsibleQuestionWrapper
                                onClick={() => toggleFaq(index)}
                            >
                                <FaqQuestion>
                                    {replaceFieldsWithValues(collapsable.text, {
                                        location,
                                        brand,
                                        nearYou,
                                        city,
                                        garage,
                                    })}
                                </FaqQuestion>
                                <Image
                                    src={
                                        activeIndex[index]
                                            ? resolvePath(
                                                  '/images/ackodrive/chevron_up.svg'
                                              )
                                            : resolvePath(
                                                  '/images/ackodrive/chevron_down.svg'
                                              )
                                    }
                                    width={16}
                                    height={16}
                                />
                            </CollapsibleQuestionWrapper>
                            {activeIndex[index] && (
                                <FaqAnswer
                                    dangerouslySetInnerHTML={{
                                        __html: replaceFieldsWithValues(
                                            collapsable.subText,
                                            {
                                                location,
                                                brand,
                                                nearYou,
                                                city,
                                                garage,
                                            }
                                        ),
                                    }}
                                />
                            )}
                        </FaqQAContainer>
                    );
                })}
            </FaqQAWrapper>
        </FaqWrapper>
    );
};

export default Faq;

const FaqWrapper = styled.div`
    display: flex;
    padding: 24px 16px;
    flex-direction: column;
    align-items: center;
    gap: 20px;
    align-self: stretch;
    background: #f0f0f6;
`;

const FaqHeading = styled.div`
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    gap: 20px;
    align-self: stretch;
    color: #121212;
    font-family: 'Euclid Circular B';
    font-size: 20px;
    font-style: normal;
    font-weight: 600;
    line-height: 28px;
    letter-spacing: -0.1px;
`;

const FaqQAWrapper = styled.div`
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    gap: 16px;
    align-self: stretch;
`;

const FaqQAContainer = styled.div`
    display: flex;
    padding: 12px;
    flex-direction: column;
    justify-content: center;
    align-items: center;
    gap: 8px;
    align-self: stretch;
    border-radius: 12px;
    background: #fff;
    border: 1px solid #e8e8e8;
`;

const CollapsibleQuestionWrapper = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: center;
    align-self: stretch;
    gap: 8px;
`;

const FaqQuestion = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: center;
    align-self: stretch;
    color: #121212;
    font-family: 'Euclid Circular B';
    font-size: 14px;
    font-style: normal;
    font-weight: 500;
    line-height: 20px;
    width: calc(100% - 24px);
`;

const FaqAnswer = styled.div`
    color: #757575;
    font-family: 'Euclid Circular B';
    font-size: 12px;
    font-style: normal;
    font-weight: 400;
    line-height: 18px;
    align-self: stretch;
    > p,
    ol {
        margin: 0;
    }
`;
